import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FloppyDisk, Plus, Trash, Lightning, ArrowsClockwise,
  FileText, CheckCircle, Warning, ArrowCounterClockwise, ShieldCheck
} from '@phosphor-icons/react';

const INV_STATUS_LABELS = {
  not_processed: 'Not Processed', pdf_uploaded: 'PDF Uploaded', extracted: 'Extracted',
  verified: 'Verified', bom_generated: 'BOM Generated', stock_calculated: 'Stock Calculated',
  ordered: 'Ordered', inward_done: 'Inward Done', issued: 'Issued', closed: 'Closed',
};

export function InventoryJODetail() {
  const { jo_number } = useParams();
  const navigate = useNavigate();
  const [jo, setJo] = useState(null);
  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [bom, setBom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingBom, setGeneratingBom] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [colors, setColors] = useState([]);
  const [bulkColor, setBulkColor] = useState('');

  const applyBulkColor = () => {
    if (!bulkColor || items.length === 0) return;
    // Sentinel "__WHITE__" means clear colour (white profile); otherwise use the colour name as-is
    const isWhite = bulkColor === '__WHITE__';
    const colorValue = isWhite ? '' : bulkColor;
    const updated = items.map(it => ({
      ...it,
      color: colorValue,
      is_color_profile: !isWhite,
    }));
    setItems(updated);
    toast.success(
      isWhite
        ? `Applied White (Clear) to all ${items.length} row(s)`
        : `Applied "${colorValue}" to all ${items.length} row(s)`
    );
  };

  const fetchData = useCallback(async () => {
    try {
      const [joRes, headerRes, itemsRes, bomRes, colRes] = await Promise.all([
        api.get(`/api/job-orders/${jo_number}`),
        api.get(`/api/inventory/jo/${jo_number}/header`),
        api.get(`/api/inventory/jo/${jo_number}/items`),
        api.get(`/api/inventory/jo/${jo_number}/bom`),
        api.get('/api/inventory/masters/colors'),
      ]);
      setJo(joRes.data);
      setHeader(headerRes.data);
      setItems(itemsRes.data);
      setBom(bomRes.data);
      setColors(colRes.data.map(c => c.name));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load JO data');
    } finally {
      setLoading(false);
    }
  }, [jo_number]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(''); // '', 'uploading', 'extracting'

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      e.target.value = '';
      return;
    }
    // Client-side size check (backend caps at 20 MB)
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadPhase('uploading');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/api/inventory/jo/${jo_number}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // PDF upload + Gemini extraction can take up to ~2 minutes for large docs
        timeout: 180000,
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
            if (pct >= 100) setUploadPhase('extracting');
          }
        },
      });
      toast.success(data.message || 'PDF uploaded and items extracted successfully');
      if (data.items) setItems(data.items);
      setValidation(null);
      fetchData();
    } catch (err) {
      if (err?.isAuthError) return;
      let msg;
      if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
        msg = 'Upload timed out. The PDF may be too large or the extraction took too long. Please try again or upload a smaller file.';
      } else if (err?.response?.status === 413) {
        msg = 'File is too large. Maximum allowed is 20 MB.';
      } else if (err?.response?.status === 400) {
        msg = err?.response?.data?.detail || 'Invalid file. Only PDF files are accepted.';
      } else if (err?.response?.status === 500) {
        msg = err?.response?.data?.detail
          ? `Extraction failed: ${err.response.data.detail}`
          : 'Server error during extraction. Please retry.';
      } else if (err?.message === 'Network Error') {
        msg = 'Network error. Check your connection and try again.';
      } else {
        msg = err?.response?.data?.detail || 'Upload failed. Please try again.';
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadPhase('');
      e.target.value = '';
    }
  };

  const updateItem = (idx, key, val) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: val };
    setItems(copy);
  };

  const addItem = () => {
    setItems([...items, {
      width: 0, height: 0, item_name: '', profile: '', shutters: 0,
      qty: 1, sqft: 0, glass_type: '', mesh_flag: false, color: '', remarks: '',
    }]);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const saveItems = async () => {
    setSaving(true);
    try {
      await api.post(`/api/inventory/jo/${jo_number}/items`, items);
      toast.success('Items saved');
      setValidation(null);
      fetchData();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const validateItems = async () => {
    setValidating(true);
    try {
      const { data } = await api.get(`/api/inventory/jo/${jo_number}/validate-items`);
      setValidation(data);
      if (data.valid) toast.success('All items mapped — ready to generate BOM');
      else toast.error(data.message);
    } catch { toast.error('Validation failed'); }
    finally { setValidating(false); }
  };

  const generateBom = async () => {
    // Always validate first
    setValidating(true);
    try {
      const { data: val } = await api.get(`/api/inventory/jo/${jo_number}/validate-items`);
      setValidation(val);
      if (!val.valid) {
        toast.error(val.message);
        setValidating(false);
        return;
      }
    } catch { toast.error('Validation failed'); setValidating(false); return; }
    setValidating(false);

    setGeneratingBom(true);
    try {
      const { data } = await api.post(`/api/inventory/jo/${jo_number}/generate-bom`);
      toast.success(data.message);
      setBom(data.bom || []);
      setActiveTab('bom');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'BOM generation failed');
    } finally { setGeneratingBom(false); }
  };

  const resetExtraction = async () => {
    if (!window.confirm('This will clear ALL extracted items and BOM data for this JO. Continue?')) return;
    try {
      await api.post(`/api/inventory/jo/${jo_number}/reset-extraction`);
      toast.success('Extraction data cleared');
      setItems([]);
      setBom([]);
      setValidation(null);
      fetchData();
    } catch { toast.error('Reset failed'); }
  };

  const resetBom = async () => {
    if (!window.confirm('This will clear BOM results but keep extracted items. Continue?')) return;
    try {
      await api.post(`/api/inventory/jo/${jo_number}/reset-bom`);
      toast.success('BOM data cleared');
      setBom([]);
      fetchData();
    } catch { toast.error('Reset failed'); }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/api/inventory/jo/${jo_number}/header`, { inventory_status: newStatus });
      toast.success(`Status updated to ${INV_STATUS_LABELS[newStatus]}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  if (loading) return <Layout><div className="py-12 text-center">Loading...</div></Layout>;
  if (!jo) return <Layout><div className="py-12 text-center text-red-500">Job Order not found</div></Layout>;

  const invStatus = header?.inventory_status || 'not_processed';

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/inventory/job-orders')} className="h-9">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-zinc-950">Inventory - {jo_number}</h1>
            <p className="text-sm text-zinc-500">{jo.customer_name} | {jo.work_location}</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm"><ArrowsClockwise size={14} /></Button>
        </div>

        {/* JO Details (Read-only) */}
        <Card className="rounded-sm shadow-sm">
          <CardHeader className="py-2 px-4 bg-zinc-50 border-b">
            <CardTitle className="text-sm font-bold text-zinc-600">Job Order Details (Read-Only)</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-zinc-500 text-xs font-bold uppercase">JO Date</span><p className="font-bold">{jo.jo_date}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Customer</span><p className="font-bold">{jo.customer_name}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Mobile</span><p>{jo.customer_mobile}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Location</span><p>{jo.work_location}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Profile Type</span><p className="font-bold">{jo.profile_type}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Glass Type</span><p>{jo.glass_type}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Windows</span><p className="font-bold">{jo.no_of_windows}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Total SQFT</span><p className="font-bold">{jo.total_sqft}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Delivery Date</span><p className="text-red-600 font-bold">{jo.delivery_date}</p></div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">JO Status</span><p className="font-bold">{jo.status?.replace(/_/g, ' ')}</p></div>
              <div>
                <span className="text-zinc-500 text-xs font-bold uppercase">Inventory Status</span>
                <p className="font-bold text-orange-600">{INV_STATUS_LABELS[invStatus]}</p>
              </div>
              <div><span className="text-zinc-500 text-xs font-bold uppercase">Executive</span><p>{jo.executive_name}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Actions Bar */}
        <Card className="rounded-sm shadow-sm">
          <CardContent className="p-3 flex flex-wrap gap-2 items-center">
            <label className="cursor-pointer">
              <input type="file" accept=".pdf" onChange={handlePdfUpload} className="hidden" disabled={uploading} />
              <span data-testid="inv-upload-pdf" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs font-bold text-white ${uploading ? 'bg-amber-400 cursor-wait' : 'bg-amber-600 hover:bg-amber-700 cursor-pointer'}`}>
                <Upload size={14} />
                {uploadPhase === 'uploading' && `Uploading ${uploadProgress}%`}
                {uploadPhase === 'extracting' && 'Extracting items...'}
                {!uploadPhase && 'Upload PDF'}
              </span>
            </label>
            <Button data-testid="inv-save-items" onClick={saveItems} disabled={saving} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Items'}
            </Button>
            <Button data-testid="inv-validate" onClick={validateItems} disabled={validating || items.length === 0} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-bold">
              <ShieldCheck size={14} className="mr-1" /> {validating ? 'Checking...' : 'Validate'}
            </Button>
            <Button data-testid="inv-gen-bom" onClick={generateBom} disabled={generatingBom || items.length === 0} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white font-bold">
              <Lightning size={14} className="mr-1" /> {generatingBom ? 'Generating...' : 'Generate BOM'}
            </Button>
            <Button data-testid="inv-reset-bom" onClick={resetBom} disabled={bom.length === 0} size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50 font-bold">
              <ArrowCounterClockwise size={14} className="mr-1" /> Reset BOM
            </Button>
            <Button data-testid="inv-reset-all" onClick={resetExtraction} size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 font-bold">
              <Trash size={14} className="mr-1" /> Reset All
            </Button>
            <div className="flex-1" />
            <select
              data-testid="inv-status-select"
              value={invStatus}
              onChange={e => updateStatus(e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white font-bold"
            >
              {Object.entries(INV_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Validation Alert */}
        {validation && !validation.valid && (
          <Card className="rounded-sm shadow-sm border-red-300 bg-red-50">
            <CardContent className="p-3">
              <div className="flex items-start gap-2">
                <Warning size={20} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-red-800 text-sm">{validation.message}</p>
                  <p className="text-xs text-red-600 mt-1">Create these items in Item Master or Item-Material Map before generating BOM:</p>
                  <div className="mt-2 space-y-1">
                    {validation.unmapped.map((u) => (
                      <div key={u.item_name} className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded font-bold">Not Mapped</span>
                        <span className="text-zinc-800 font-bold">{u.item_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {validation && validation.valid && (
          <Card className="rounded-sm shadow-sm border-emerald-300 bg-emerald-50">
            <CardContent className="p-2 flex items-center gap-2">
              <CheckCircle size={18} weight="bold" className="text-emerald-600" />
              <p className="text-sm font-bold text-emerald-700">All {validation.mapped_count} items mapped — ready for BOM generation</p>
            </CardContent>
          </Card>
        )}

        {/* Tab selector */}
        <div className="flex gap-1 border-b border-zinc-200">
          {['items', 'bom'].map(tab => (
            <button
              key={tab}
              data-testid={`inv-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-bold transition-colors ${
                activeTab === tab
                  ? 'border-b-2 border-orange-600 text-orange-700'
                  : 'text-zinc-500 hover:text-zinc-800'
              }`}
            >
              {tab === 'items' ? `Inventory Items (${items.length})` : `BOM Results (${bom.length})`}
            </button>
          ))}
        </div>

        {/* Items Tab */}
        {activeTab === 'items' && (
          <Card className="rounded-sm shadow-sm">
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2 mb-3 items-center">
                <Button onClick={addItem} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus size={14} className="mr-1" /> Add Row
                </Button>
                {items.length > 0 && (
                  <div className="flex items-center gap-2 ml-auto bg-orange-50 border border-orange-200 rounded px-2 py-1">
                    <span className="text-xs font-bold text-orange-700 uppercase tracking-wide">Bulk Colour:</span>
                    <select
                      data-testid="inv-bulk-color-select"
                      value={bulkColor}
                      onChange={e => setBulkColor(e.target.value)}
                      className="h-7 text-xs border border-orange-300 rounded px-2 bg-white font-bold text-orange-700"
                    >
                      <option value="">-- Select Colour --</option>
                      <option value="__WHITE__">White (Clear)</option>
                      {colors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Button
                      data-testid="inv-apply-bulk-color"
                      onClick={applyBulkColor}
                      disabled={!bulkColor}
                      size="sm"
                      className="h-7 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold"
                    >
                      Apply to All Rows
                    </Button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-50 z-10">
                    <tr className="border-b">
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left w-8">#</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left min-w-[160px]">Item Name</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-16">W(mm)</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-16">H(mm)</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left min-w-[80px]">Profile</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-14">Shut</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-12">Qty</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-14">SQFT</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left min-w-[80px]">Glass</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 w-10">Mesh</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left min-w-[60px]">Color</th>
                      <th className="p-1.5 font-bold uppercase text-zinc-500 text-left min-w-[80px]">Remarks</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={`${item.item_name}-${idx}`} className="border-b hover:bg-zinc-50">
                        <td className="p-1 text-zinc-400">{idx + 1}</td>
                        <td className="p-1"><Input value={item.item_name || ''} onChange={e => updateItem(idx, 'item_name', e.target.value)} className="h-7 text-xs" /></td>
                        <td className="p-1"><Input type="number" value={item.width || ''} onChange={e => updateItem(idx, 'width', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-16" /></td>
                        <td className="p-1"><Input type="number" value={item.height || ''} onChange={e => updateItem(idx, 'height', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-16" /></td>
                        <td className="p-1"><Input value={item.profile || ''} onChange={e => updateItem(idx, 'profile', e.target.value)} className="h-7 text-xs" /></td>
                        <td className="p-1"><Input type="number" value={item.shutters || ''} onChange={e => updateItem(idx, 'shutters', parseInt(e.target.value) || 0)} className="h-7 text-xs w-14" /></td>
                        <td className="p-1"><Input type="number" value={item.qty || ''} onChange={e => updateItem(idx, 'qty', parseInt(e.target.value) || 0)} className="h-7 text-xs w-12" /></td>
                        <td className="p-1"><Input type="number" value={item.sqft || ''} onChange={e => updateItem(idx, 'sqft', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-14" /></td>
                        <td className="p-1"><Input value={item.glass_type || ''} onChange={e => updateItem(idx, 'glass_type', e.target.value)} className="h-7 text-xs" /></td>
                        <td className="p-1 text-center"><input type="checkbox" checked={item.mesh_flag || false} onChange={e => updateItem(idx, 'mesh_flag', e.target.checked)} /></td>
                        <td className="p-1">
                          {(item.color === 'Colour' || item.is_color_profile) ? (
                            <select
                              value={item.color || ''}
                              onChange={e => {
                                const val = e.target.value;
                                updateItem(idx, 'color', val);
                                updateItem(idx, 'is_color_profile', val && val !== '' && val.toLowerCase() !== 'white');
                              }}
                              className="h-7 text-xs border rounded px-1 w-full bg-orange-50 border-orange-300 font-bold text-orange-700"
                            >
                              <option value="Colour">-- Select Color --</option>
                              {colors.map(c => <option key={c} value={c}>{c}</option>)}
                              <option value="">White (Clear)</option>
                            </select>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Input value={item.color || ''} onChange={e => updateItem(idx, 'color', e.target.value)} className="h-7 text-xs flex-1" placeholder="White" />
                              {!item.color && <span className="text-zinc-300 text-xs">W</span>}
                            </div>
                          )}
                        </td>
                        <td className="p-1"><Input value={item.remarks || ''} onChange={e => updateItem(idx, 'remarks', e.target.value)} className="h-7 text-xs" /></td>
                        <td className="p-1">
                          <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-500"><Trash size={12} /></Button>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr><td colSpan={13} className="p-8 text-center text-zinc-400">
                        No items yet. Upload a PDF to auto-extract or add manually.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* BOM Tab */}
        {activeTab === 'bom' && (
          <Card className="rounded-sm shadow-sm">
            <CardContent className="p-3">
              {bom.length === 0 ? (
                <div className="p-8 text-center text-zinc-400">
                  No BOM generated. Save items → Validate → Generate BOM.
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-zinc-50 z-10">
                      <tr className="border-b">
                        <th className="p-1.5 font-bold uppercase text-zinc-500 text-left">Item</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500 text-left">Material</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500 text-left">Category</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">UQC</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">SQFT</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">Adj SQFT</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">+Wastage</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">Net Req</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">Gross Req</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">Available</th>
                        <th className="p-1.5 font-bold uppercase text-zinc-500">Shortage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bom.map((b) => {
                        const bomKey = `${b.line_no}-${b.material_name}`;
                        const shortage = Math.max(0, (b.gross_qty || 0) - (b.available_stock || 0));
                        return (
                          <tr key={bomKey} className={`border-b hover:bg-zinc-50 ${shortage > 0 ? 'bg-red-50' : ''}`}>
                            <td className="p-1.5 font-bold text-zinc-800">{b.item_name}</td>
                            <td className="p-1.5 text-zinc-700">{b.material_name}</td>
                            <td className="p-1.5 text-zinc-500">{b.category}</td>
                            <td className="p-1.5 text-center font-bold">{b.uqc}</td>
                            <td className="p-1.5 text-center">{b.item_sqft ?? '--'}</td>
                            <td className="p-1.5 text-center text-blue-700 font-bold">{b.adjusted_sqft?.toFixed(2) ?? '--'}</td>
                            <td className="p-1.5 text-center text-amber-600">+{b.wastage_sqft ?? 0}</td>
                            <td className="p-1.5 text-center font-bold">{b.net_qty?.toFixed(2)}</td>
                            <td className="p-1.5 text-center font-bold text-orange-700">{b.gross_qty?.toFixed(2)}</td>
                            <td className={`p-1.5 text-center font-bold ${(b.available_stock || 0) < (b.gross_qty || 0) ? 'text-red-600' : 'text-emerald-600'}`}>
                              {b.available_stock?.toFixed(2)}
                            </td>
                            <td className={`p-1.5 text-center font-bold ${shortage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {shortage > 0 ? shortage.toFixed(2) : 'OK'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
